import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import TableSearchInput from "./TableSearchInput";
import FilterChips, { buildFilterChips, ResetFiltersButton } from "./FilterChips";
import useSearchField from "./useSearchField";

/**
 * Mobile counterpart of the DataTable toolbar: full-width search, optional extra filter controls,
 * a reset button and the active-filter chips. Bind it to the same `useTableFilters` object as the
 * desktop table so both views share one filter state.
 */
function MobileFilterBar({ filters, search, extra, filterLabels, formatFilterValue, columns }) {
    const { t } = useTranslation("table");
    const { searchKey, field, onFieldChange } = useSearchField(search, filters);

    const chips = buildFilterChips({
        filters: filters.filters,
        columns,
        searchFields: search?.fields,
        searchKey: search?.key,
        filterLabels,
        formatFilterValue,
        t,
    });

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                {search && (
                    <TableSearchInput
                        className="min-w-0 flex-1"
                        size="large"
                        value={filters.filters[searchKey] ?? ""}
                        onChange={(text) => filters.setFilter(searchKey, text)}
                        placeholder={search.placeholder}
                        fields={search.fields}
                        field={field}
                        onFieldChange={onFieldChange}
                    />
                )}
                <ResetFiltersButton filters={filters} size="large" iconOnly className="shrink-0" />
            </div>
            {extra && <div className="flex flex-wrap items-center gap-2 [&>*]:min-w-0 [&>*]:flex-1">{extra}</div>}
            <FilterChips chips={chips} onRemove={filters.clearFilter} onClearAll={filters.resetFilters} />
        </div>
    );
}

MobileFilterBar.propTypes = {
    filters: PropTypes.shape({
        filters: PropTypes.object.isRequired,
        setFilter: PropTypes.func.isRequired,
        clearFilter: PropTypes.func.isRequired,
        resetFilters: PropTypes.func.isRequired,
    }).isRequired,
    search: PropTypes.shape({
        key: PropTypes.string,
        placeholder: PropTypes.string,
        fields: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.node })),
    }),
    extra: PropTypes.node,
    filterLabels: PropTypes.object,
    formatFilterValue: PropTypes.func,
    columns: PropTypes.array,
};

export default MobileFilterBar;
